import type { HeadingPluginElement, HeadingPluginPropertyOptions } from '../types';
import styled, { css } from 'styled-components';

import type { GetRenderElementComponentType } from '../../../utils/getRenderElement';
import type { THeadingLevel } from '../constants';
import {
  getHeadingFontSize,
  getHeadingTextTransform,
  getHeadingTextDecoration,
} from '../utils/styling';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useReporterStyles } from 'hooks/useReporterStyles';
import type { Styles } from 'generated/graphql';
import { Children, isValidElement, type HTMLAttributes } from 'react';
import { useSelected } from 'slate-react';
import { telemetryContextFor } from 'modules/Apollo/types';
import SvgIcon from '@mui/material/SvgIcon';

export const getHeadingIconAsSvgIcon = ({ Component }: { Component: any }) => {
  const HeadingIcon = (props: any) => (
    <SvgIcon component={Component} inheritViewBox fontSize="small" {...props} />
  );

  return HeadingIcon;
};

type HeadingWrapperProps = {
  children: React.ReactNode;
  level: THeadingLevel;
};

export const HeadingWrapper = ({ children, level }: HeadingWrapperProps): React.ReactElement => {
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterSettings = data?.me?.reporterSettings;

  const style: Styles = useReporterStyles(reporterSettings);
  const headingStyles = [...(style?.headingStyles ?? [])];
  const fontSize = getHeadingFontSize(headingStyles, level);
  const textTransform = getHeadingTextTransform(headingStyles, level);
  const textDecoration = getHeadingTextDecoration(headingStyles, level);

  // Currently using a single font family for whole report, dictated by the body style.
  const fontFamily = style?.bodyStyle?.fontFamily;

  type HeadingChildProps = {
    text?: {
      text?: string;
    };
  };

  const headingName = Children.toArray(children)
    .map((child) => {
      if (!isValidElement(child)) {
        return '';
      }
      const props = child.props as HeadingChildProps;
      return props.text?.text ?? '';
    })
    .join('');
  const selected = useSelected();

  return (
    <p
      css={css`
        margin: 0;
        display: inline;
        text-transform: ${textTransform};
        font-family: ${fontFamily};
        font-size: ${fontSize}px;
        font-weight: ${textDecoration?.isBold === true ? 'bold' : 'normal'};
        font-style: ${textDecoration?.isItalic === true ? 'italic' : 'normal'};
        text-decoration: ${textDecoration?.isUnderline === true ? 'underline' : 'none'};
        &:before {
          content: '';
          display: block;
        }
      `}
      data-testid={`heading-${level}-${headingName.toLowerCase()}`}
      data-editor-element={`heading-${level}`}
      data-selected={selected}
    >
      {children}
    </p>
  );
};

export const HeadingEditable: GetRenderElementComponentType<
  HeadingPluginPropertyOptions,
  HeadingPluginElement
> = ({ element, children, ...rest }): React.ReactElement => {
  const slateAttributes = rest.attributes as HTMLAttributes<HTMLSpanElement>;
  return (
    <span {...slateAttributes}>
      <HeadingWrapper level={element.level}>{children}</HeadingWrapper>
    </span>
  );
};

export const HeadingRenderer = styled(
  ({ element, children }: { element: HeadingPluginElement; children: React.ReactNode }) => (
    <HeadingWrapper level={element.level}>{children}</HeadingWrapper>
  )
)`
  white-space: pre-wrap;
`;

HeadingWrapper.displayName = 'HeadingWrapper';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HeadingWrapper);
